import { RING_MS } from "@rhema/client";

type AudioContextCtor = typeof AudioContext;

/**
 * Plays a two-tone ring made with the Web Audio API, with no sound files. Returns a function that
 * stops it. It stops by itself after 45 seconds, when the server stops ringing too.
 */
export function startRingtone(maxMs = RING_MS): () => void {
  const Ctor: AudioContextCtor | undefined =
    globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
  if (!Ctor) return () => {};
  let ctx: AudioContext;
  try {
    ctx = new Ctor();
  } catch {
    return () => {};
  }
  void ctx.resume().catch(() => undefined);
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  const oscillators = [440, 480].map((frequency) => {
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = frequency;
    o.connect(gain);
    o.start();
    return o;
  });

  // Two short rings, then a pause, every three seconds.
  const ring = () => {
    const t = ctx.currentTime;
    const g = gain.gain;
    g.cancelScheduledValues(t);
    for (const start of [0, 0.6]) {
      g.setValueAtTime(0, t + start);
      g.linearRampToValueAtTime(0.12, t + start + 0.03);
      g.setValueAtTime(0.12, t + start + 0.4);
      g.linearRampToValueAtTime(0, t + start + 0.43);
    }
  };
  ring();
  const interval = setInterval(ring, 3000);
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(interval);
    clearTimeout(limit);
    for (const o of oscillators) {
      try {
        o.stop();
      } catch {
        // Already stopped.
      }
    }
    void ctx.close().catch(() => undefined);
  };
  const limit = setTimeout(stop, maxMs);
  return stop;
}
